import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, FormControlLabel, Icon, Switch, Tooltip, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { useAsyncFn } from 'react-use';
import AppTable from 'components/AppTable';
import ModalBusyContainer from 'components/ModalBusyContainer';
import { deleteTrainingPart, getTrainingPartsList } from 'http/trainingParts';
import {
  deleteProvisionedTrainingPart,
  getProvisionedTrainingPartsList,
} from 'http/hyperAdminProvisioned';
import { getCompanyTrainingModuleSettings, toggleCompanyTrainingModuleMethod } from 'http/trainingModules';
import {
  buildProvisionedTrainingPartCreatePath,
  buildProvisionedTrainingPartEditPath,
  routesAliases,
} from 'router/routesAliases';
import { actions as modalActions } from 'redux/modal';
import { getConfirmationModalConfig, resolveApiErrorMessage, transformTableData } from 'utils';
import { ROLES } from 'appConstants';
import { isLmsPreviewActionDisabled } from 'pages/Templates/helpers/lmsPreviewAction';
import getTrainingPartsTableConfig from './helpers/getTrainingPartsTableConfig';
import TrainingPartPreviewLanguageMenu from './TrainingPartPreviewLanguageMenu';

const useStyles = makeStyles(theme => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  addPartTooltip: {
    fontSize: theme.typography.body2.fontSize,
    lineHeight: theme.typography.body2.lineHeight,
    maxWidth: 320,
    borderRadius: theme.spacing(1),
  },
}));

const SORT_FIELD_API_MAP = {
  versionCode: 'versionSort',
  status: 'statusSort',
  complianceFrom: 'complianceFrom',
  availableFrom: 'availableFrom',
  updatedAt: 'updatedAt',
};

const DEFAULT_SORTING = {
  field: 'complianceFrom',
  dir: 'asc',
};

const TrainingPartsTable = ({
  trainingModuleId,
  trainingMethod,
  readOnly = false,
  showCompanyMethodToggle = false,
  canToggleMethod = false,
  provisionedCompanyId = null,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation('common');
  const userRole = useSelector(state => state.auth.profile?.role);
  const tableConfig = useMemo(() => getTrainingPartsTableConfig(), []);
  const [sorting, setSorting] = useState(DEFAULT_SORTING);
  const [previewMenu, setPreviewMenu] = useState({
    anchorEl: null,
    trainingPartId: null,
  });
  const isProvisionedCompanyContext =
    Number.isFinite(Number(provisionedCompanyId)) && Number(provisionedCompanyId) > 0;

  const closeModal = useCallback(() => dispatch(modalActions.closeModal()), [dispatch]);

  const buildSortParams = useCallback(({ field, dir }) => {
    const apiField = SORT_FIELD_API_MAP[field];
    if (!apiField) {
      return {};
    }

    return {
      [apiField]: dir.toUpperCase(),
    };
  }, []);

  const [partsState, fetchParts] = useAsyncFn(async () => {
    const params = {
      trainingModuleId,
      trainingMethod,
      page: 0,
      pageSize: 100,
      ...buildSortParams(sorting),
    };
    if (isProvisionedCompanyContext) {
      return getProvisionedTrainingPartsList(provisionedCompanyId, params);
    }
    return getTrainingPartsList(params);
  }, [
    trainingModuleId,
    trainingMethod,
    sorting,
    buildSortParams,
    isProvisionedCompanyContext,
    provisionedCompanyId,
  ]);

  const [settingsState, fetchSettings] = useAsyncFn(async () => {
    return getCompanyTrainingModuleSettings(trainingModuleId);
  }, [trainingModuleId]);

  const showErrorModal = useCallback(
    message => {
      const [config, element] = getConfirmationModalConfig({
        confirmTitle: t('buttons.ok'),
        confirmAction: closeModal,
        rejectAction: closeModal,
        message: resolveApiErrorMessage(message, {
          t,
          fallbackMessage: t('errors.serverError'),
        }),
      });
      dispatch(modalActions.openModal(config, element));
    },
    [closeModal, dispatch, t],
  );

  const [toggleState, toggleMethod] = useAsyncFn(
    async isEnabled => {
      const result = await toggleCompanyTrainingModuleMethod(trainingModuleId, trainingMethod, isEnabled);
      if (!result?.error && !(result?.statusCode && result.statusCode >= 400)) {
        await fetchSettings();
        return result;
      }
      showErrorModal(result.message);
      return result;
    },
    [fetchSettings, showErrorModal, trainingMethod, trainingModuleId],
  );

  const refreshParts = useCallback(() => {
    fetchParts();
  }, [fetchParts]);

  useEffect(() => {
    if (trainingModuleId) {
      fetchParts();
    }
  }, [trainingModuleId, trainingMethod, fetchParts]);

  useEffect(() => {
    if (showCompanyMethodToggle && trainingModuleId) {
      fetchSettings();
    }
  }, [fetchSettings, partsState.value, showCompanyMethodToggle, trainingModuleId]);

  const [deleteState, removePart] = useAsyncFn(
    async id => {
      const result = isProvisionedCompanyContext
        ? await deleteProvisionedTrainingPart(Number(provisionedCompanyId), id)
        : await deleteTrainingPart(id);
      if (!result.error && !result.statusCode) {
        closeModal();
        refreshParts();
        return result;
      }
      showErrorModal(result.message);
      return result;
    },
    [closeModal, isProvisionedCompanyContext, provisionedCompanyId, refreshParts, showErrorModal],
  );

  const openCreatePage = useCallback(() => {
    if (isProvisionedCompanyContext) {
      history.push({
        pathname: buildProvisionedTrainingPartCreatePath(provisionedCompanyId, trainingModuleId, trainingMethod),
        state: location.state?.returnTab != null ? { returnTab: location.state.returnTab } : undefined,
      });
      return;
    }
    history.push({
      pathname: `${routesAliases.createTrainingPart}/${trainingModuleId}/training-parts/create/${trainingMethod}`,
    });
  }, [history, isProvisionedCompanyContext, location.state?.returnTab, provisionedCompanyId, trainingMethod, trainingModuleId]);

  const openEditPage = useCallback(
    part => {
      if (isProvisionedCompanyContext) {
        history.push({
          pathname: buildProvisionedTrainingPartEditPath(
            provisionedCompanyId,
            trainingModuleId,
            part.id,
            trainingMethod,
          ),
          state: location.state?.returnTab != null ? { returnTab: location.state.returnTab } : undefined,
        });
        return;
      }
      history.push({
        pathname: `${routesAliases.editTrainingPart}/${trainingModuleId}/training-parts/edit/${part.id}/${trainingMethod}`,
      });
    },
    [history, isProvisionedCompanyContext, location.state?.returnTab, provisionedCompanyId, trainingMethod, trainingModuleId],
  );

  const handleDeletePart = useCallback(
    partId => {
      const [config, element] = getConfirmationModalConfig({
        title: t('trainingParts.deleteModalTitle'),
        confirmAction: () => removePart(partId),
        rejectAction: closeModal,
        message: t('trainingParts.deleteConfirmationMessage'),
      });
      dispatch(modalActions.openModal(config, element));
    },
    [closeModal, dispatch, removePart, t],
  );

  const handleColumnSorting = useCallback((field, dir) => {
    setSorting({ field, dir });
  }, []);

  const tableActions = useMemo(() => {
    const actions = [];

    if (trainingMethod === 'theory' && (userRole === ROLES.HYPER || userRole === ROLES.SUPER)) {
      actions.push({
        type: 'icon',
        icon: 'launch',
        title: t('buttons.open', 'Open in LMS'),
        onClick: event => {
          const partId = Number(event.currentTarget.dataset.key);
          if (!Number.isNaN(partId)) {
            setPreviewMenu({
              anchorEl: event.currentTarget,
              trainingPartId: partId,
            });
          }
        },
        getDisabled: isLmsPreviewActionDisabled,
      });
    }

    actions.push(
      readOnly
        ? {
            type: 'icon',
            icon: 'visibility',
            onClick: event => {
              const partId = Number(event.currentTarget.dataset.key);
              const part = (partsState.value?.items || []).find(item => item.id === partId);
              if (part) {
                openEditPage(part);
              }
            },
          }
        : {
            type: 'icon',
            icon: 'edit',
            onClick: event => {
              const partId = Number(event.currentTarget.dataset.key);
              const part = (partsState.value?.items || []).find(item => item.id === partId);
              if (part) {
                openEditPage(part);
              }
            },
          },
    );

    if (!readOnly) {
      actions.push({
        type: 'icon',
        icon: 'delete',
        color: 'error',
        getDisabled: rowData => {
          const part = (partsState.value?.items || []).find(item => item.id === Number(rowData.id));
          return part?.status !== 'draft' && part?.status !== 'scheduled';
        },
        getTitle: rowData => {
          const part = (partsState.value?.items || []).find(item => item.id === Number(rowData.id));
          return part?.status !== 'draft' && part?.status !== 'scheduled'
            ? t('trainingPart.deleteOnlyDraftOrScheduled')
            : undefined;
        },
        onClick: event => {
          const part = (partsState.value?.items || []).find(
            item => item.id === Number(event.currentTarget.dataset.key),
          );
          if (part?.status !== 'draft' && part?.status !== 'scheduled') {
            return;
          }
          handleDeletePart(Number(event.currentTarget.dataset.key));
        },
      });
    }

    return actions;
  }, [handleDeletePart, openEditPage, partsState.value, readOnly, t, trainingMethod, userRole]);

  const items = partsState.value?.items || [];
  const hasDraftPart = items.some(item => item.status === 'draft');
  const [columns, rows] = transformTableData(tableConfig, items);
  const methodSetting = (settingsState.value?.methodSettings || []).find(
    item => item.trainingMethod === trainingMethod,
  );
  const isMethodEnabled = methodSetting?.isEnabled ?? true;
  const canEnableMethod =
    methodSetting?.hasActivePart ??
    items.some(item => item.status === 'active' || item.status === 'scheduled');
  const isLoading = partsState.loading || deleteState.loading || toggleState.loading;

  return (
    <Box>
      <Box className={classes.header}>
        <Typography variant="h6" className={classes.sectionTitle}>
          {t(`trainingModules.tabs.${trainingMethod}`)}
        </Typography>
        <Box className={classes.headerActions}>
          {showCompanyMethodToggle ? (
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={isMethodEnabled}
                  onChange={event => toggleMethod(event.target.checked)}
                  disabled={
                    !canToggleMethod ||
                    settingsState.loading ||
                    toggleState.loading ||
                    (!isMethodEnabled && !canEnableMethod)
                  }
                />
              }
              label={
                !canEnableMethod && !isMethodEnabled
                  ? t('trainingModules.methodToggleNoParts')
                  : t('trainingModules.methodToggleLabel')
              }
            />
          ) : null}
          {!readOnly ? (
            <Tooltip
              title={hasDraftPart ? t('trainingParts.addDisabledWhileDraft') : ''}
              classes={{ tooltip: classes.addPartTooltip }}
            >
              <span>
                <Button
                  color="primary"
                  variant="contained"
                  startIcon={<Icon>add</Icon>}
                  onClick={openCreatePage}
                  disabled={hasDraftPart}
                >
                  {t('trainingParts.actionAdd')}
                </Button>
              </span>
            </Tooltip>
          ) : null}
        </Box>
      </Box>
      <ModalBusyContainer busy={isLoading}>
        <AppTable
          columns={columns}
          data={rows}
          totalCount={items.length}
          rowsPerPage={items.length || 10}
          actions={tableActions}
          onColumnSorting={handleColumnSorting}
          extraActionsKeys={trainingMethod === 'theory' ? ['hasMoodleLink'] : []}
        />
      </ModalBusyContainer>
      <TrainingPartPreviewLanguageMenu
        anchorEl={previewMenu.anchorEl}
        trainingPartId={previewMenu.trainingPartId}
        onClose={() => setPreviewMenu({ anchorEl: null, trainingPartId: null })}
      />
    </Box>
  );
};

TrainingPartsTable.propTypes = {
  trainingModuleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  trainingMethod: PropTypes.oneOf(['theory', 'ojt', 'practical']).isRequired,
  readOnly: PropTypes.bool,
  showCompanyMethodToggle: PropTypes.bool,
  canToggleMethod: PropTypes.bool,
  provisionedCompanyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

TrainingPartsTable.defaultProps = {
  readOnly: false,
  showCompanyMethodToggle: false,
  canToggleMethod: false,
  provisionedCompanyId: null,
};

export default TrainingPartsTable;
